import React, {useState} from 'react';
import {View, StyleSheet, Image} from 'react-native';
import CircleChessLogo from '@/Assets/Images/Svgs/home_page/logo.svg';
import OnboardingTournament from '@/Assets/Images/Svgs/tournament_banner/banner_main.svg';
import CheckIcon from '@/Assets/Images/Svgs/tournament_banner/check_icon.svg';
import CCText from '@/Atoms/CCText';
import {FontSizes, isPhone} from '@/Utils/Dimensions';
import CCColors from '@/Utils/CCColors';
import CCButton from '@/Atoms/CCButton';
import SlideModal from '@/Atoms/SlideModal';
import {MaterialIcons} from '@expo/vector-icons';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import Spacer from '@/Atoms/Spacer';
import useSetTournamentIntent from '@/Hooks/GetTournaments/useSetTournamentIntent';

const JoinTournamentScreen = ({onStartOnboarding}) => {
  const {isMobileOrTab} = useMediaQuery();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);

  const {mutate: setTournamentIntent, isLoading} = useSetTournamentIntent();

  const handleJoinIntent = (intent: boolean) => {
    setTournamentIntent(
      {intent},
      {
        onSuccess: () => {
          setHasJoined(intent);
          setIsOpenModal(true);
        },
        onError: error => {
          console.error('Failed to set tournament intent', error);
        },
      },
    );
  };

  return (
    <View style={styles.container}>
      {isMobileOrTab && (
        <View style={styles.header}>
          <Image source={CircleChessLogo} style={styles.logo} />
          <View style={styles.headerRow}>
            <View style={styles.headerTextContainer}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.TextColor?.Dark}
                style={styles.headerTitle}>
                Welcome to CircleChess
              </CCText>
              <CCText
                fontSize={FontSizes[12]}
                color={CCColors?.TextColor?.Primary}
                style={styles.headerSubtitle}>
                Join the World's Biggest Ever Chess Tournament, Compete Against
                the Best and Win Prize Money!
              </CCText>
            </View>
            <Image source={OnboardingTournament} style={styles.banner} />
          </View>
        </View>
      )}
      <View style={styles.content}>
        {!isMobileOrTab && (
          <>
            <Image source={CircleChessLogo} style={styles.logo} />
            <Spacer spacing={99} />
          </>
        )}
        <CCText
          fontSize={FontSizes[isMobileOrTab ? 16 : 24]}
          color={CCColors?.TextColor?.Dark}
          style={styles.question}>
          Would you like to join the CircleChess Global Star Championship 2025?
        </CCText>
        {/* <CCText
          fontSize={FontSizes[14]}
          color={CCColors?.TextColor?.Primary}
          style={styles.description}>
          CircleChess is hosting a tournament! Don’t miss your chance to compete
          join now!
        </CCText> */}
        <View style={styles.bullets}>
          {[
            'Platform : CircleChess',
            'Format : 3 mins + 1 sec',
            '50,000+ players',
            'Free Entry',
            '₹5 Lakh Prize Pool',
            '20th July, 7 PM',
          ].map((text, index) => {
            const isHighlighted =
              text === 'Free Entry' || text === '₹5 Lakh Prize Pool';
            return (
              <View style={styles.bulletItem} key={index}>
                <Image source={CheckIcon} style={styles.checkIcon} />
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors?.TextColor?.LightGrey}
                  fontWeight={isHighlighted ? 'bold' : undefined}
                  style={styles.bulletText}>
                  {text}
                </CCText>
              </View>
            );
          })}
        </View>
        <View style={styles.buttonContainer}>
          <CCButton
            disabled={isLoading}
            onPress={() => handleJoinIntent(true)}
            style={styles.yesButton}
            titleStyle={{
              fontSize: FontSizes[isMobileOrTab ? 16 : 14],
              fontWeight: '500',
              color: CCColors?.TextColor?.Dark,
            }}>
            Yes
          </CCButton>
          <CCButton
            disabled={isLoading}
            onPress={() => handleJoinIntent(false)}
            style={styles.noButton}
            titleStyle={{
              fontSize: FontSizes[isMobileOrTab ? 16 : 14],
              fontWeight: '500',
              color: CCColors?.TextColor?.Dark,
            }}>
            No
          </CCButton>
        </View>
        <SlideModal
          type={isMobileOrTab ? 'bottom' : 'center'}
          visible={isOpenModal}
          customStyle={{height: isMobileOrTab ? '50%' : undefined}}
          minWidth={402}
          onClose={() => setIsOpenModal(false)}>
          <View style={styles.modalContainer}>
            <View style={styles.modalCheckIcon}>
              <MaterialIcons name="check" size={32} color={CCColors.White} />
            </View>
            <CCText
              fontSize={FontSizes[18]}
              color={CCColors.TextColor.Dark}
              style={styles.titleText}>
              {hasJoined ? 'Awesome!' : 'Great!'}
            </CCText>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.Primary}
              textAlign="center"
              style={styles.subtitleText}>
              {hasJoined
                ? "You've successfully registered for the tournament."
                : "We'll guide you through the learning journey."}
            </CCText>
            <CCButton
              titleStyle={{
                fontSize: FontSizes[isMobileOrTab ? 16 : 14],
                fontWeight: '500',
                color: CCColors.TextColor.Dark,
              }}
              onPress={() => {
                onStartOnboarding();
                setIsOpenModal(false);
              }}>
              {hasJoined
                ? 'Continue to Tournament Onboarding'
                : 'Continue to Learning Onboarding'}
            </CCButton>
          </View>
        </SlideModal>
      </View>
    </View>
  );
};

export default JoinTournamentScreen;

const styles = StyleSheet.create({
  container: {
    marginTop: !isPhone ? 66 : undefined,
    paddingHorizontal: !isPhone ? 120 : undefined,
    flex: 1,
  },
  header: {
    backgroundColor: CCColors?.Primary?.lightBrown,
    alignItems: 'flex-start',
    paddingLeft: 16,
    paddingTop: 20,
  },
  logo: {
    width: isPhone ? 121 : 203,
    height: isPhone ? 24 : 40,
    resizeMode: 'contain',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontWeight: '700',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontWeight: '400',
  },
  banner: {
    width: 146,
    height: 158,
    alignSelf: 'center',
  },
  content: {
    marginHorizontal: 16,
    marginTop: 32,
  },
  question: {
    fontWeight: '500',
    marginBottom: isPhone ? 16 : 24,
  },
  description: {
    fontWeight: '400',
    marginBottom: isPhone ? 16 : 24,
  },
  bullets: {
    marginBottom: 24,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  checkIcon: {
    width: 24,
    height: 24,
    resizeMode: 'contain',
  },
  bulletText: {
    fontWeight: '400',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  yesButton: {
    flex: 1,
    alignItems: 'center',
  },
  noButton: {
    flex: 1,
    backgroundColor: CCColors?.Primary?.Light,
    alignItems: 'center',
  },
  modalContainer: {
    alignItems: 'center',
    paddingHorizontal: isPhone ? 0 : 24,
    paddingBottom: 40,
  },
  modalCheckIcon: {
    height: 72,
    width: 72,
    backgroundColor: CCColors.Green,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 36,
    marginBottom: 12,
  },
  titleText: {
    fontWeight: '600',
    marginBottom: 8,
  },
  subtitleText: {
    maxWidth: 402,
    fontWeight: '400',
    marginBottom: 24,
    lineHeight: isPhone ? 20 : 24,
    paddingHorizontal: isPhone ? 24 : 0,
  },
});
